import { useCallback, useEffect, useRef } from 'react';
import { toastProgress } from '../components/toastService';

/* [LIVE-CAPSULE 2026-11-02, user] The Live capsule for queued saves: when a change has to wait behind another, the "added to
   queue" message is now the Live capsule (components/toastService — dark glass with a running green light) and it STAYS while
   the queued changes save, counting down ("Saving 2 more changes…"), then closes when the last one lands. It was a blue message
   that vanished after 2 s while the saves could still be running. The "updated" message of each save is unchanged. One capsule
   for the whole app (one page at a time). */
let live: ReturnType<typeof toastProgress> | null = null;

/**
 * [SAVE-QUEUE 2026-09-09, user] One ordered queue for every write a page makes.
 * ============================================================================================
 * "when i update any value in the inventory or the payment tab … i want that thing to be fast
 * also, but the data should be stored properly and at the right place; if someone makes the
 * changes again and again then all should go to the queue, and the popup should say 'changes
 * added to queue' … and if someone is making the change, then that change should also happen.
 * No error to that."
 *
 * WHAT WAS THERE BEFORE, measured, not assumed:
 *   • Inventory already painted optimistically and already had a queue — but only the cell
 *     editor used it. The dropdown editor, the renewal modal and the bulk edit fired straight
 *     at the API, so a dropdown change made while a cell save was in flight raced it for the
 *     payments trigger: the exact deadlock class the queue was built for (1,555 "Deadlock found"
 *     errors in the server log, up to 97 in a day).
 *   • Payments' website/email save painted optimistically too — and then SILENTLY DROPPED a
 *     second edit to the same cell while the first was still saving (`savingRef` early return,
 *     no message). That is a lost change, which is the one thing the user said must never
 *     happen. Mapping saves were never serialised at all.
 *   • Nothing anywhere told the user their edit was waiting behind another one.
 *
 * WHAT THIS DOES. Every write is chained onto the previous one, so N edits become N sequential
 * requests in the order they were made — none is dropped, none can deadlock against another,
 * and the last one written is the last one made. `enqueue` reports how many writes are AHEAD
 * of the new one at the moment it is added; the page uses that to say "added to queue" when
 * the user has outrun the network, and "updated" when each one actually lands.
 *
 * WHY THE SERVER IS UNTOUCHED. The requests, their payloads and their handlers are identical
 * to before; only their timing changes, from "all at once" to "one after another". Every
 * route was already correct for sequential requests, so nothing about where or how data is
 * stored moves. A per-page queue cannot protect two different PEOPLE editing the same row —
 * that was never protected before either and is not what was asked.
 *
 * WHAT IT CANNOT SURVIVE, and the guard for it: a queued (not yet sent) edit lives only in
 * this tab. Closing or refreshing the tab mid-queue would lose it, so while anything is
 * pending the browser asks before leaving. The window is small — each write goes out the
 * instant the previous one returns, ~0.3–1 s — but "small" is not "never".
 */

export interface EnqueueResult<T> {
  /** Resolves/rejects with the job's own result, once its turn comes and it finishes. */
  promise: Promise<T>;
  /** Writes that must finish before this one starts. 0 = it starts right now. */
  ahead: number;
}

export function useSaveQueue() {
  // The chain every job is appended to. Kept alive through rejections (see enqueue) so one
  // failed save can never block every later edit.
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  // Jobs not yet finished — in flight plus waiting. A ref, not state: nothing needs to
  // re-render when it changes, and reading it inside the unload guard must be synchronous.
  const pending = useRef(0);

  const enqueue = useCallback(<T,>(job: () => Promise<T>): EnqueueResult<T> => {
    const ahead = pending.current;
    pending.current += 1;
    const run = () => job().finally(() => {
      pending.current -= 1;
      // [LIVE-CAPSULE] the queue drained → the capsule goes; else it counts down
      if (live) {
        if (pending.current <= 0) { live.close(); live = null; }
        else live.update(`Saving ${pending.current} more change${pending.current === 1 ? '' : 's'}…`);
      }
    });
    // `run` on BOTH branches: a rejected predecessor must not stop this job from running.
    const promise = chain.current.then(run, run);
    chain.current = promise.catch(() => { /* keep the chain alive */ });
    return { promise, ahead };
  }, []);

  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (pending.current > 0) {
        e.preventDefault();
        // Modern browsers ignore custom text and show their own "changes may not be saved"
        // prompt; setting returnValue is what triggers it.
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, []);

  return { enqueue };
}

/**
 * The "you outran the network" message, worded once for every page. Shown ONLY when a write
 * had to wait behind another; a write that starts immediately gets the normal "updated" toast
 * when it lands, exactly as before. Short-lived and blue, so it reads as status rather than
 * as a second confirmation of the same change.
 */
export function queuedToast(what: string, ahead: number): void {
  const after = ahead === 1 ? 'the current change' : `${ahead} changes ahead`;
  const text = `${what} added to queue — saves after ${after}`;
  if (live) live.update(text); else live = toastProgress(text); // [LIVE-CAPSULE] stays until the queue is empty
}
